import type { MeetingQuestion } from '../cubits/meeting/MeetingQuestion'
import type { RoomStateAnswer } from '../models/RoomStateAnswer'
import { type SlideQuestion } from '../models/SlideQuestion'
import { SlideQuestionType } from '../models/SlideQuestionType'
import type { RoomStateEngagement } from '../models/RoomStateEngagement'
import type { RoomStateRubricResult } from '../models/RoomStateRubricResult'
import type { SlideRubric } from '../models/SlideRubric'
import type { PromptRubricType } from '../models/PromptRubricType'
import type { RoomState } from '../models/RoomState'
import type { SectionAssignment } from '../models/SectionAssignment'
import { CompletionMetric } from '../firestore/SectionAssignment/schema'
import { RubricType as RubricTypeInt } from '../types'
import type { BloomScore, PassFailScore } from '../types'
import type { FirestoreAssignmentRubric } from '../firestore/AssignmentRubric/schema'
import { defaultScoreMappingForRubricType } from './assignmentRubricDefaults'

/**
 * Resolve the weight a rubric contributes for a user's role, given the rubric's
 * `weights` union. Returns null when the rubric does not apply to the user's
 * role — i.e. a `byRole` rubric whose map does not list that role. This
 * subsumes the legacy `assignmentRoles` applicability filter for custom rubrics.
 */
function roleWeightForUser(
  weights: FirestoreAssignmentRubric['weights'],
  userRole: string | null
): number | null {
  if ('allRoles' in weights) return weights.allRoles
  if (userRole === null) return null
  return userRole in weights.byRole ? weights.byRole[userRole] : null
}

/** returns a float between 0 and 1 for sorting questions
 * returns a binary (0 , 1) for all other questions types
 */
export function getQuizAnswerScore({
  answer,
  question,
}: {
  answer: RoomStateAnswer
  question: SlideQuestion
}): number {
  let sum = 0
  // switch on question type
  switch (question.questionType) {
    case SlideQuestionType.sorting:
      // map over the answers and calculate the average
      // score for the sorting question
      // by getting the absolute difference between the
      // value in each answerList[] value and the index
      // answerList may be null so handle that case by excluding
      // it from the calculation
      if (answer.data.answerList) {
        sum = answer.data.answerList
          .map((value, index) => Math.abs(index - value))
          .reduce((value, element) => value + element)
      }

      return (
        (question.getWorstSortingScore - (sum ?? 0)) /
        question.getWorstSortingScore
      )
    case SlideQuestionType.multipleChoice:
      if (answer.data.answer === question.data.correct) {
        return 1
      } else {
        return 0
      }
    case SlideQuestionType.poll:
    case SlideQuestionType.customPoll:
    case SlideQuestionType.title:
      return 0
  }
  return 0
}

export function isMeetingQuestionScorable(
  question: MeetingQuestion | SlideQuestion
) {
  const isIndividualQuizQuestion =
    question.slideId !== undefined && question.slideId !== ''

  if (!isIndividualQuizQuestion) return false

  return (
    // we do not use sorting for scoring - we accidentally had it here
    // but this is incorrect - leaving for reference
    // question.questionType === SlideQuestionType.sorting ||
    question.questionType === SlideQuestionType.multipleChoice
  )
}

export function getAggregateQuizAnswerScore({
  answers,
  question,
}: {
  answers: RoomStateAnswer[]
  question: SlideQuestion
}) {
  switch (question.questionType) {
    case SlideQuestionType.sorting: {
      const sums = answers
        .map((answer) => answer.data.answerList)
        .filter((list) => Array.isArray(list))
        .map((answerList) =>
          answerList!
            .map((entry, index) => Math.abs(index - entry))
            .reduce((value, element) => value + element)
        )
      const average =
        sums.length === 0
          ? 0
          : sums.reduce((value, element) => value + element) / sums.length
      return average
    }
    case SlideQuestionType.multipleChoice: {
      const correct = answers
        .map((answer) => getQuizAnswerScore({ answer, question }))
        .reduce((value, element) => value + element)
      return correct / answers.length
    }
    default:
      return 0
  }
}

export function displayedPercentage(percentage: number) {
  return percentage.toFixed(0)
}

/// create histogram from the getMultipleChoicePerformance
/// in 20% wide buckets, return a map of bucket and count
/// always return a map of 5 buckets whose key is `n %`
/// put the results in the correct bucket
export function getSortingQuizHistogram({
  answers,
  question,
}: {
  answers: Array<RoomStateAnswer>
  question: SlideQuestion
}) {
  const histogram: Map<string, number> = new Map()
  const scores: Map<string, number> = new Map()
  const resultsInBuckets: Map<string, Array<RoomStateAnswer>> = new Map()

  // create a list of buckets starting from the question's worst score
  // and decreasing to zero. Buckets should be equal in size
  // and start with 0 members
  const bucketSize = question.getWorstSortingScore / 10
  for (let n = 0; n < 10; n++) {
    histogram.set(
      displayedPercentage(question.getWorstSortingScore - bucketSize * n),
      0
    )
  }
  // for each answer, calculate the score and put it in the correct bucket
  for (const answer of answers) {
    const score =
      question.getWorstSortingScore -
      getQuizAnswerScore({ answer, question }) * question.getWorstSortingScore
    scores.set(answer.data.userId, score)

    // loop over entries in reverse order and find the first bucket
    // that the score is less than or equal to bucket name + bucketSize
    const entries = Array.from(histogram.entries()).reverse()
    for (const entry of entries) {
      const [key, value] = entry
      const bucket = Number(key)
      if (score <= bucket + bucketSize) {
        histogram.set(key, value + 1)
        resultsInBuckets.set(key, [
          ...(resultsInBuckets.get(key) ?? []),
          answer,
        ])
        break
      }
    }
  }
  // sort all the results in the buckets by score descending
  for (const entry of resultsInBuckets.entries()) {
    const [entryKey, entryValue] = entry
    const valueSorted = entryValue.sort((a, b) => {
      const aScore = scores.get(a.data.userId) ?? 0
      const bScore = scores.get(b.data.userId) ?? 0
      return bScore - aScore
    })
    resultsInBuckets.set(entryKey, valueSorted)
  }

  return { histogram, resultsInBuckets }
}

export const getCoefficientOfVariation = (data: number[]) => {
  if (!data.length) return 0
  const n = data.length
  const mean = data.reduce((a, b) => a + b) / n
  if (mean === 0) return NaN
  const standardDeviation = Math.sqrt(
    data.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n
  )
  return standardDeviation / mean
}

/**
 * Calculate quiz score for a user
 * `quizScore` internal scalar (on assignment)
 *
 * Returns null when the score is NaN (e.g., division by zero when there are
 * only sorting questions and no MC questions), which allows the UI to display
 * '-' instead of '0%'.
 */
export function calculateQuizScoreForUser({
  userId,
  quizScores,
}: {
  userId: string
  quizScores: Map<string, number | null>
}): number | null {
  const score = quizScores.get(userId)
  if (score === null) return null
  // NaN indicates division by zero (no MC questions), return null to show '-'
  if (Number.isNaN(score)) return null
  if (score === undefined || score < 0) return 0
  return score
}

/**
 * Calculate total rubric score for a user
 * `totalRubricScore` internal scalar (on assignment)
 * `Critical Thinking` for copy/UI
 */
export function calculateTotalRubricScoreForUserFloat({
  userId,
  rubricResults,
  rubricTypes = [],
  roomState,
  assignmentRubrics,
}: {
  userId: string
  rubricResults: Map<string, Map<SlideRubric, RoomStateRubricResult[]>>
  rubricTypes?: PromptRubricType[]
  roomState?: RoomState
  /**
   * Per-rubric custom config keyed by rubricId. When provided (custom rubrics
   * enabled), applicability and weight come from each rubric's `weights`, and
   * the score→percentage conversion uses `scoreMapping`. When omitted, behaviour
   * is identical to the legacy equal-weight / linear average.
   */
  assignmentRubrics?: Map<string, FirestoreAssignmentRubric>
}): number | null {
  const userRubricResults = rubricResults.get(userId)
  if (!userRubricResults) return null

  const rubricTypesMap = rubricTypes.reduce<Record<string, PromptRubricType>>(
    (acc, rubricType) => {
      acc[rubricType.id] = rubricType
      return acc
    },
    {}
  )

  // check if user has a role
  const assignmentRoles = roomState?.data.assignmentRoles ?? {}
  const userRole = userId in assignmentRoles ? assignmentRoles[userId] : null
  const wasAloneInExperience = roomState && roomState.data.userIds.length === 1

  const applicableRubricEntries = Array.from(
    userRubricResults.entries()
  ).filter(([rubric, results]) => {
    // exclude if no results
    if (!results || results.length === 0) return false

    const assignmentRubric = assignmentRubrics?.get(rubric.id)
    if (assignmentRubric) {
      // custom rubric: applicability is derived from `weights` (a byRole rubric
      // that does not list the user's role does not apply to them)
      if (roleWeightForUser(assignmentRubric.weights, userRole) === null) {
        return false
      }
    } else if (
      rubric.data.assignmentRoles &&
      rubric.data.assignmentRoles.length
    ) {
      // legacy: exclude if user doesn't have the required role
      if (!userRole || !rubric.data.assignmentRoles.includes(userRole)) {
        return false
      }
    }

    // exclude if user was alone and rubric excludes solo
    const rubricTypeObj = rubricTypesMap
      ? rubricTypesMap[rubric.data.rubricType]
      : null

    if (rubricTypeObj?.excludeSoloExperiences && wasAloneInExperience)
      return false

    return true
  })

  if (!applicableRubricEntries.length) return null

  let weightedSum = 0
  let totalWeight = 0

  for (const [rubric, results] of applicableRubricEntries) {
    const assignmentRubric = assignmentRubrics?.get(rubric.id)
    // The rubric's stored per-assignment mapping, else the canonical default
    // for its type (BL-4721 step 3: assignments own their mappings; an
    // unpinned rubric of an eligible type scores under the CURRENT defaults,
    // not the retired linear formula). Types with no default mapping
    // (demoDelivery) keep the linear conversion below.
    const scoreMapping =
      assignmentRubric?.scoreMapping ??
      defaultScoreMappingForRubricType(rubric.data.rubricType)

    const bestPossible = (() => {
      switch (rubric.data.rubricType) {
        // Legacy Bloom's grading collapsed any non-zero score to full credit
        // (max of 1). With a mapping present — stored or canonical — use the
        // true max so each Bloom's level can map to a distinct percentage;
        // the canonical Bloom's mapping reproduces the collapse.
        case RubricTypeInt.blooms:
          return scoreMapping
            ? (rubricTypesMap[RubricTypeInt.blooms]?.maxScore ?? 6)
            : 1
        case RubricTypeInt.passFail:
          return rubricTypesMap[RubricTypeInt.passFail]?.maxScore ?? 1
        case RubricTypeInt.decisionMaking:
          return rubricTypesMap[RubricTypeInt.decisionMaking]?.maxScore ?? 4
        case RubricTypeInt.evidence:
          return rubricTypesMap[RubricTypeInt.evidence]?.maxScore ?? 4
        case RubricTypeInt.peerEngagement:
          return rubricTypesMap[RubricTypeInt.peerEngagement]?.maxScore ?? 4
        case RubricTypeInt.criticalThinking:
          return rubricTypesMap[RubricTypeInt.criticalThinking]?.maxScore ?? 4
        case RubricTypeInt.demoDelivery:
          return rubricTypesMap[RubricTypeInt.demoDelivery]?.maxScore ?? 2
      }
    })()

    const highest = results.reduce<BloomScore | PassFailScore>(
      (acc, result) => {
        // do not allow scores above the best possible
        if (result.data.score > acc)
          return Math.min(result.data.score, bestPossible)
        return acc
      },
      0 as BloomScore
    )

    // score→percentage: the resolved mapping when present (stored, else
    // canonical), linear only for types with no mapping or a score the
    // mapping does not cover
    const scorePercent = (() => {
      if (!scoreMapping) return highest / bestPossible
      const mapped = scoreMapping[String(highest)]
      return mapped !== undefined ? mapped / 100 : highest / bestPossible
    })()

    // weight: the rubric's role weight when custom, else equal (1). Applicable
    // custom rubrics always resolve to a non-null weight (filtered above).
    const weight = assignmentRubric
      ? (roleWeightForUser(assignmentRubric.weights, userRole) ?? 1)
      : 1

    weightedSum += scorePercent * weight
    totalWeight += weight
  }

  // preserves legacy semantics: all-zero scores → 0 (also guards 0/0)
  if (!weightedSum) return 0

  return weightedSum / totalWeight
}

/**
 * Calculate attendance score for a user
 * `attendanceScore` internal scalar (on assignment)
 * `Attendance` for copy/UI
 */
export function calculateAttendanceScoreForUser({
  userId,
  engagementData,
}: {
  userId: string
  engagementData: Map<string, RoomStateEngagement>
}): number {
  const engagement = engagementData.get(userId)
  if (!engagement || engagement.isEmpty) return 0
  // Present for zero seconds is not attendance. Zeroed no-participation
  // records (BL-4030 fallback) exist for the whole roster, so doc existence
  // alone no longer implies the student joined.
  return engagement.data.durationPresent ? 1 : 0
}

/**
 * Calculate the median engagement duration from a collection of engagement entries.
 * Shared by InstructorAssignmentCubit and MeetingResultsCubit.
 */
export function calculateMedianEngagementDuration(
  engagementEntries: RoomStateEngagement[]
): number {
  const durations = engagementEntries
    .map((e) => (e.isEmpty ? null : e.data.durationPresent))
    .filter((d): d is number => typeof d === 'number')
    .sort((a, b) => a - b)

  if (!durations.length) return 0
  if (durations.length < 3) return durations[0]

  const mid = Math.floor(durations.length / 2)
  return durations.length % 2 === 0
    ? (durations[mid - 1] + durations[mid]) / 2
    : durations[mid]
}

/**
 * Per-user camera-on seconds: time present minus camera-off time, floored at 0.
 * A participant who never published video has no camera-on time regardless of
 * `cameraOffTime` (which stays 0 when no VIDEO_OFF ever fires, so it cannot be
 * derived from cameraOffTime alone). `videoPublished === undefined` (legacy doc)
 * falls through to `durationPresent - cameraOffTime`; see the PRD "Known
 * limitations" for the accepted legacy imprecision.
 */
function cameraOnSecondsForEngagement(engagement: RoomStateEngagement): number {
  const { durationPresent, cameraOffTime, videoPublished } = engagement.data
  if (videoPublished === false) return 0
  return Math.max(0, (durationPresent || 0) - (cameraOffTime || 0))
}

/**
 * Calculate engagement/participation score for a user
 * `participationScore` internal scalar (on assignment)
 * `Completion` for copy/UI
 *
 * The Completion criterion has two measurements, selected per assignment by
 * `completionMetric`. Both divide the same way: by the group median in-session
 * time (`medianEngagementDuration`), with an identical 0.95 buffer, a 0.5 floor
 * for anyone who attended, and a 1.0 cap; a participant who never joined or was
 * present for zero seconds scores 0 under either. Only the numerator (indicator) changes with the metric:
 *  - timeInSession (default/legacy): time present.
 *  - cameraOnTime: camera-on time (time present minus camera-off time).
 *
 * Keeping the divisor as the in-session median means a short attendee cannot
 * reach full Completion under either metric, and camera-off time only lowers the
 * numerator, not the baseline the group is measured against.
 */
export function calculateEngagementScoreForUser({
  userId,
  engagementData,
  medianEngagementDuration,
  completionMetric,
}: {
  userId: string
  engagementData: Map<string, RoomStateEngagement>
  medianEngagementDuration: number
  completionMetric?: CompletionMetric
}): number {
  const engagement = engagementData.get(userId)
  if (!engagement || engagement.isEmpty) return 0 // never joined => 0, either metric
  // Present for zero seconds is not attendance. A zeroed no-participation
  // record (BL-4030 fallback) would otherwise ride the zero-median
  // full-credit branch below to 100%.
  if (!engagement.data.durationPresent) return 0

  const indicator =
    completionMetric === CompletionMetric.cameraOnTime
      ? cameraOnSecondsForEngagement(engagement)
      : engagement.data.durationPresent

  const effectiveMedian = 0.95 * medianEngagementDuration

  const relativeToMedian =
    effectiveMedian <= 0
      ? 1 //full credit
      : indicator
        ? indicator / effectiveMedian
        : 0 //no credit

  return 0.5 + Math.min(0.5, relativeToMedian * 0.5)
}

/**
 * Calculate scalar raw score data for a user
 */
export function calculateScalarRawScoreDataForUser({
  userId,
  scalars,
  quizScores,
  rubricResults,
  rubricTypes,
  roomState,
  engagementData,
  medianEngagementDuration,
  completionMetric,
  assignmentRubrics,
  nullIfNoScalars = true,
}: {
  userId: string
  scalars: SectionAssignment['data']['assignmentGradingScalars']
  quizScores: Map<string, number | null>
  rubricResults: Map<string, Map<SlideRubric, RoomStateRubricResult[]>>
  rubricTypes?: PromptRubricType[]
  roomState?: RoomState
  engagementData: Map<string, RoomStateEngagement>
  medianEngagementDuration: number
  completionMetric?: CompletionMetric
  assignmentRubrics?: Map<string, FirestoreAssignmentRubric>
  nullIfNoScalars?: boolean
}) {
  if (!scalars && nullIfNoScalars) return null

  const scores: {
    participationScore: number
    quizScore?: number | null
    totalRubricScore?: number
    totalScore: number
    attendanceScore: number
  } = {
    totalScore: 0,
    participationScore: 0,
    attendanceScore: 0,
  }

  // Calculate quiz score
  const quizScore = calculateQuizScoreForUser({ userId, quizScores })

  scores.quizScore = quizScore
  // Only add to total if quizScore is not null (i.e., there are MC questions)
  if (scalars && quizScore !== null)
    scores.totalScore += quizScore * scalars.quizScore

  // Calculate rubric score
  const rubricScore =
    calculateTotalRubricScoreForUserFloat({
      userId,
      rubricResults,
      rubricTypes,
      roomState,
      assignmentRubrics,
    }) || 0
  scores.totalRubricScore = rubricScore
  if (scalars) scores.totalScore += rubricScore * scalars.totalRubricScore

  // Calculate participation/engagement score
  const participationScore = calculateEngagementScoreForUser({
    userId,
    engagementData,
    medianEngagementDuration,
    completionMetric,
  })
  scores.participationScore = participationScore
  if (scalars)
    scores.totalScore += participationScore * scalars.participationScore

  // Calculate attendance score
  const attendanceScore = calculateAttendanceScoreForUser({
    userId,
    engagementData,
  })
  scores.attendanceScore = attendanceScore
  if (scalars) scores.totalScore += attendanceScore * scalars.attendanceScore

  return scores
}
